/*
 * Free shipping picks. Where it renders, and when it hides, is documented in
 * snippets/free-shipping-picks.liquid.
 *
 * Progressive enhancement over plain links to /cart/add, one per product.
 * Anything this script can't handle is left to the link, which adds the
 * product and returns to the cart page.
 *
 * Links rather than forms because on the cart page this list renders inside
 * Dawn's <form id="cart">, and the HTML parser discards a nested form. See
 * the note beside the markup in snippets/free-shipping-picks.liquid.
 *
 * This deliberately mirrors assets/gift-box-offer.js rather than sharing code
 * with it. The two could be one generic "add from the cart" element, but that
 * file is live, signed off and loaded only when the gift box is enabled, so
 * folding this into it would make the picks depend on a setting that has
 * nothing to do with them. If both are ever reworked, merge them then.
 *
 * Two hosts, each refreshed with the sections it already declares in
 * getSectionsToRender(), so the picks follow any future change there:
 *
 * - <cart-notification> (the add-to-cart popup): renders the new sections in
 *   place. It deliberately does not call renderContents(), because that calls
 *   open() on a popup that is already open. The transitionend listener open()
 *   leaves behind would then fire on close and trap focus inside the hidden
 *   popup.
 *
 * - <cart-items> (the cart page): renders every section except those inside
 *   <cart-items>. Those are refreshed by CartItems#onCartUpdate when
 *   cart-update is published below, and rendering them here too would do it
 *   twice.
 */
if (!customElements.get('free-shipping-picks')) {
  customElements.define(
    'free-shipping-picks',
    class FreeShippingPicks extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', this.onClick.bind(this));
      }

      onClick(event) {
        const button = event.target.closest('.free-shipping-picks__button');
        if (!button) return;

        // Let the browser handle a click the customer meant as navigation
        // (new tab, new window, download), so the link still behaves like one.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

        const notification = this.closest('cart-notification');
        const cartItems = notification ? null : document.querySelector('cart-items');
        const host = notification || cartItems;
        if (typeof host?.getSectionsToRender !== 'function') return;

        event.preventDefault();

        if (button.getAttribute('aria-disabled') === 'true') return;

        this.setLoading(button, true);
        this.showError('');

        const variantId = button.dataset.variantId;
        const sections = notification
          ? host.getSectionsToRender()
          : host.getSectionsToRender().filter((section) => !host.contains(document.getElementById(section.id)));

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify({
          id: Number(variantId),
          quantity: 1,
          sections: sections.map((section) => section.section || section.id),
          sections_url: window.location.pathname,
        });

        const linesUpdate = this.createCartLinesUpdateEvent(variantId, notification ? 'dialog' : 'cart');

        fetch(routes.cart_add_url, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              const message = response.description || response.message || window.cartStrings?.error;
              this.showError(message);
              this.setLoading(button, false);
              this.dispatchCartErrorEvent(message, 'INVALID');
              linesUpdate?.reject(new Error(message));
              return;
            }

            this.resolveCartLinesUpdate(linesUpdate);

            if (notification) {
              this.renderNotification(notification, response);
            } else {
              this.renderCartPage(sections, response);
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'free-shipping-picks',
              productVariantId: variantId,
              cartData: response,
            });
          })
          .catch((error) => {
            console.error(error);
            this.showError(window.cartStrings?.error);
            this.setLoading(button, false);
            this.dispatchCartErrorEvent(error.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdate?.reject(error);
          });
      }

      // Same loop as CartNotification#renderContents, without the open() call
      // (see the header comment). The popup now shows the product just added,
      // and the picks are gone with it because the threshold has been reached.
      renderNotification(notification, response) {
        notification.cartItemKey = response.key;
        notification.getSectionsToRender().forEach((section) => {
          const mount = document.getElementById(section.id);
          const html = response.sections?.[section.id];
          if (!mount || !html) return;
          mount.innerHTML = notification.getSectionInnerHTML(html, section.selector);
        });

        // The button that had focus was just replaced. Re-trap focus on the
        // new contents, which also moves focus to the dialog itself.
        trapFocus(notification.notification);
      }

      // Replaces this element too: the footer comes back with the bar in its
      // qualified state and the picks gone. Focus follows to the bar message
      // so the result is read out, since the control that had focus is gone.
      renderCartPage(sections, response) {
        sections.forEach((section) => {
          const root = document.getElementById(section.id);
          const html = response.sections?.[section.section];
          if (!root || !html) return;
          const target = root.querySelector(section.selector) || root;
          const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
          if (source) target.innerHTML = source.innerHTML;
        });

        const message = document.querySelector('.cart__footer .free-shipping-bar__message');
        if (!message) return;
        message.setAttribute('tabindex', '-1');
        message.focus();
      }

      setLoading(button, loading) {
        button.classList.toggle('loading', loading);
        button.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
        if (loading) {
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('aria-disabled');
        }
      }

      showError(message) {
        const error = this.querySelector('.free-shipping-picks__error');
        if (!error) return;
        error.textContent = message || '';
        error.hidden = !message;
      }

      // Shopify storefront standard events, mirroring assets/product-form.js so
      // a pick counts as an add to cart like any other product.
      createCartLinesUpdateEvent(variantId, context) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context,
            lines: [{ merchandiseId: variantId, quantity: 1 }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!deferred || !CartLinesUpdateEvent) return;

        fetch(`${routes.cart_url}.json`)
          .then((response) => response.json())
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((error) => deferred.reject(error));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }
    }
  );
}
